import type { Workspace } from '@carboncopyco/types';
import { Separator, TooltipBubble } from '@carboncopyco/ui';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { WorkspaceAvatar } from '../WorkspaceAvatar';
import { AllWorkspacesButton } from './AllWorkspacesButton';
import { DashboardButton } from './DashboardButton';
import { PlaygroundButton } from './PlaygroundButton';

export interface WorkspacesSidebarProps {
  workspaces: Workspace[];
  selectedWorkspace: Workspace['id'] | null;
  switchWorkspace: (id: string) => void;
  navigateToAllWorkspaces: () => void;
  isInAllWorkspaces: boolean;
  isInDashboard?: boolean;
  navigateToDashboard?: () => void;
}

export const WorkspacesSidebar = ({
  workspaces,
  selectedWorkspace,
  switchWorkspace,
  navigateToAllWorkspaces,
  isInAllWorkspaces,
  isInDashboard = false,
  navigateToDashboard,
}: WorkspacesSidebarProps) => {
  const playground = useMemo(
    () =>
      workspaces.find(({ workspaceType }) => workspaceType === 'playground'),
    [workspaces]
  );
  const flags = useFlags();

  return (
    <div className="flex-shrink-0 bg-ds-accent3-100 w-ds-15 border-r">
      <div
        className={classNames(
          'flex flex-col items-center gap-5 h-full',
          flags?.analyticsDashboard ? 'pt-5 pb-2.5' : 'py-5'
        )}
      >
        <div className="flex flex-col gap-5 overflow-y-auto w-full py-1">
          {workspaces
            .filter(({ workspaceType }) => workspaceType !== 'playground')
            .slice(0, 10)
            .map((workspace, index) => {
              const { workspaceName: name, id } = workspace;
              const isSelected = String(id) === selectedWorkspace;
              return (
                <div
                  key={id}
                  className="flex w-full justify-center relative"
                >
                  {isSelected && (
                    <div className="bg-ds-primary-300 w-1 absolute left-0 h-full" />
                  )}
                  <TooltipBubble
                    title={name}
                    placement="right"
                  >
                    <button
                      data-ds-id={`workspace-avatar-${index}`}
                      onClick={() => {
                        switchWorkspace(id);
                      }}
                    >
                      <WorkspaceAvatar
                        workspace={workspace}
                        size="medium"
                      />
                    </button>
                  </TooltipBubble>
                </div>
              );
            })}
        </div>
        {!flags?.analyticsDashboard && (
          <Separator
            horizontal
            middle
          />
        )}
        <div
          className={classNames(
            'flex flex-col gap-1.5',
            flags?.analyticsDashboard ? 'gap-2.5' : 'gap-1.5 mt-auto'
          )}
        >
          {playground && (
            <PlaygroundButton
              isSelected={playground.id === selectedWorkspace}
              onClick={() => {
                switchWorkspace(playground.id);
              }}
            />
          )}
          <AllWorkspacesButton
            aria-label="Copy link"
            isSelected={isInAllWorkspaces}
            onClick={navigateToAllWorkspaces}
          />
        </div>
        {flags?.analyticsDashboard && (
          <div className="mt-auto flex flex-col w-full items-center  gap-2.5">
            <Separator horizontal />
            <DashboardButton
              onClick={navigateToDashboard}
              isSelected={isInDashboard}
            />
          </div>
        )}
      </div>
    </div>
  );
};
